// Lightweight Meta Pixel loader.
//
// The official fbq snippet (and the `react-facebook-pixel` package) end with
// `document.getElementsByTagName('script')[0].parentNode.insertBefore(...)`.
// In sandboxed/preview environments there may be no <script> tag in the DOM,
// so that first element is `undefined` and reading `.parentNode` throws
// "Cannot read properties of undefined (reading 'parentNode')", crashing the
// whole app. This implementation appends the pixel script directly to
// <head> with appendChild, which never touches a sibling's parentNode.

type FbqArgs = unknown[]

interface FbqFn {
  (...args: FbqArgs): void
  callMethod?: (...args: FbqArgs) => void
  queue?: FbqArgs[]
  push?: unknown
  loaded?: boolean
  version?: string
}

declare global {
  interface Window {
    fbq?: FbqFn
    _fbq?: FbqFn
  }
}

let initialized = false

function ensureFbq(): FbqFn | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null
  }

  if (window.fbq) return window.fbq

  const fbq: FbqFn = function (...args: FbqArgs) {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, args)
    } else {
      fbq.queue!.push(args)
    }
  }

  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.queue = []

  window.fbq = fbq
  window._fbq = fbq

  // Load the Facebook events script safely via appendChild.
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  const parent = document.head || document.body
  if (parent) {
    parent.appendChild(script)
  }

  return fbq
}

export function initPixel(pixelId: string): void {
  try {
    const fbq = ensureFbq()
    if (!fbq || initialized) return
    fbq('init', pixelId)
    initialized = true
  } catch (err) {
    console.warn('Meta Pixel init skipped:', err)
  }
}

export function trackPageView(): void {
  try {
    if (window.fbq) window.fbq('track', 'PageView')
  } catch (err) {
    console.warn('Meta Pixel PageView skipped:', err)
  }
}

export function trackEvent(
  event: string,
  data?: Record<string, unknown>,
): void {
  try {
    if (window.fbq) window.fbq('track', event, data)
  } catch (err) {
    console.warn('Meta Pixel track skipped:', err)
  }
}
